import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import styled from 'styled-components';

import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import { ResponsiveTable, Table, TableContent, Th } from '@/components/Table';
import { EytvFilesResource, PaginatedResponse } from '@/types/resources';
import { api } from '@/utils/api';

import EytvFilesTableRow from './EytvFilesTableRow';

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: center;

    &:nth-child(1) {
      width: 20%;
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: 20%;
      font-weight: 700;
    }
    &:nth-child(3) {
      width: 20%;
    }
    &:nth-child(4) {
      width: 20%;
    }
    &:nth-child(5) {
      width: calc(20% - 56px);
    }
    &:last-child {
      width: 56px;
      position: relative;
    }
  }
  td {
    padding: 12px;
  }
`;

const EytvFilesTable = () => {
  const fetchEytvFiles = ({ pageParam = `/v1/eytv-files` }) => {
    return api.get<never, PaginatedResponse<EytvFilesResource>>(pageParam);
  };

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<EytvFilesResource>>(
    ['eytv_files'],
    fetchEytvFiles,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const eytvFiles = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<EytvFilesResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>CSV Date</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th>Video Count</Th>
              <Th>Files</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={6}
              isError={isError}
              hasData={!!eytvFiles?.length}
            >
              {eytvFiles.map((eytvFile) => (
                <EytvFilesTableRow key={eytvFile.id} eytvFile={eytvFile} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default EytvFilesTable;
